import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Stack
} from '@mui/material';
import { authService } from '../../services/authService';
import AddIcon from '@mui/icons-material/Add';

function NewOffre() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [entreprises, setEntreprises] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  const [newTechnology, setNewTechnology] = useState('');
  
  // Ajout des options de durée
  const dureeOptions = [
    '1 mois',
    '2 mois',
    '3 mois',
    '4 mois',
    '5 mois',
    '6 mois'
  ];
  
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    duree: '',
    remuneration: '',
    dateDebut: '',
    localisation: '',
    entreprise_id: '',
    type: 'stage'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Charger la liste des entreprises
        const entreprisesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (entreprisesResponse.ok) {
          const data = await entreprisesResponse.json();
          // Utiliser data.data pour accéder au tableau des entreprises
          setEntreprises(data.data || []);
        }

        // Pour le moment, on désactive le chargement des technologies
        // jusqu'à ce que l'API soit prête
        setTechnologies([]);
        
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddTechnology = () => {
    if (newTechnology.trim()) {
      const newTech = {
        nom_techno: newTechnology.trim()
      };
      if (!selectedTechnologies.some(tech => tech.nom_techno === newTech.nom_techno)) {
        setSelectedTechnologies(prev => [...prev, newTech]);
      }
      setNewTechnology('');
    }
  };

  const handleDeleteTechnology = (techToDelete) => {
    setSelectedTechnologies(prev => 
      prev.filter(tech => tech.nom_techno !== techToDelete.nom_techno)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const offreData = {
        ...formData,
        technologies: selectedTechnologies.map(tech => tech.nom_techno)
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(offreData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la création de l\'offre');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/offres');
      }, 2000);
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de la création de l'offre");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Créer une nouvelle offre
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Offre créée avec succès !
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Titre"
            name="titre"
            value={formData.titre}
            onChange={handleChange}
            required
            margin="normal"
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Entreprise</InputLabel>
            <Select
              name="entreprise_id"
              value={formData.entreprise_id}
              onChange={handleChange}
              required
              label="Entreprise"
            >
              {entreprises && entreprises.map((entreprise) => (
                <MenuItem 
                  key={entreprise.id_entreprises} 
                  value={entreprise.id_entreprises}
                >
                  {entreprise.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Technologies requises
            </Typography>
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                mb: 2,
                py: 2,
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {selectedTechnologies.map((tech, index) => (
                <Chip
                  key={index}
                  label={tech.nom_techno}
                  onDelete={() => handleDeleteTechnology(tech)}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '1rem',
                    height: 36,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                />
              ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Nouvelle technologie"
                value={newTechnology}
                onChange={(e) => setNewTechnology(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTechnology();
                  }
                }}
                size="small"
              />
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTechnology}
                variant="contained"
                disabled={!newTechnology.trim()}
              >
                Ajouter
              </Button>
            </Box>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Durée du stage</InputLabel>
            <Select
              name="duree"
              value={formData.duree}
              onChange={handleChange}
              required
              label="Durée du stage"
            >
              {dureeOptions.map((duree) => (
                <MenuItem key={duree} value={duree}>
                  {duree}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Rémunération"
            name="remuneration"
            value={formData.remuneration}
            onChange={handleChange}
            required
            margin="normal"
            type="number"
          />

          <TextField
            fullWidth
            label="Date de début"
            name="dateDebut"
            type="date"
            value={formData.dateDebut}
            onChange={handleChange}
            required
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            fullWidth
            label="Localisation"
            name="localisation"
            value={formData.localisation}
            onChange={handleChange}
            required
            margin="normal"
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/offres')}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Création...' : 'Créer l\'offre'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

export default NewOffre; 